import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Grid, IconButton } from '@mui/material';
import { RemoveCircleOutlined as DeleteIcon } from '@mui/icons-material';

import { useTreatmentPlanEditable } from '../hooks/useTreatmentPlanEditable';
import { medicalHistoryActions } from '../../../../store';
import { FHAutocomplete, FHInputField } from '../../../base';
import { MedicalHistoryRowSchema } from './validationSchema';
import { validateSchema, validateSync } from '../../../../helpers/validation-helpers';
import { Themes, usStates } from '../../../../constants';
import { FORTA_BLUE } from '../../../../utils/colors';
import { diagnosisOptions, errorStateConstants } from './medicalHistoryConstants';
import { debounce } from '../../../../helpers';

import type { IDiagnosis, IOption } from '../../../../utils/types';
import type { ChangeEvent } from 'react';

interface MedicalHisotoryRowProps {
  diagnosis?: IDiagnosis | null;
  medicalHistoryId: number;
  nextIndex?: number;
  onRemove?: () => void;
}

function MedicalHistoryRow({
  diagnosis,
  nextIndex,
  medicalHistoryId,
  onRemove,
}: MedicalHisotoryRowProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const editable = useTreatmentPlanEditable();
  const { current: treatmentPlan } = useSelector((state: any) => state.treatmentPlans);
  const { loading, unfinishedDiagnosis, diagnoses, unsavedDiagnoses } = useSelector(
    (state: any) => state.medicalHistories,
  );
  const [filteredDiagnosisOptions, setFilteredDiagnosisOptions] =
    useState<Array<string>>(diagnosisOptions);

  const [diagnosisRow, setDiagnosisRow] = useState<IDiagnosis>(unfinishedDiagnosis);
  const [errors, setErrors] = useState<any>({});

  const updateErrorCount = (errors: any) => {
    const errorCount = Object.values(errors).filter((value) => value).length;
    dispatch(
      medicalHistoryActions.updateErrorState({
        [`${errorStateConstants.medicalHistoryRow}-${diagnosis ? diagnosis.id : 'new'}`]:
          errorCount,
      }),
    );
  };

  const debouncedErrorUpdater = useCallback(debounce(updateErrorCount, 500), []);

  useEffect(() => {
    if (!diagnosis) return;
    setDiagnosisRow(diagnosis);
  }, [diagnosis]);
  useEffect(() => {
    if (!diagnoses) return;
    const selectedDiagnosisOptions: { [key: string]: boolean } = {};
    [...diagnoses, ...unsavedDiagnoses].forEach((diagnosis: IDiagnosis) => {
      selectedDiagnosisOptions[diagnosis.diagnosis_name] = true;
    });
    if (unfinishedDiagnosis) {
      selectedDiagnosisOptions[unfinishedDiagnosis.diagnosis_name] = true;
    }
    setFilteredDiagnosisOptions(
      diagnosisOptions.filter((option: string) => !(option in selectedDiagnosisOptions)),
    );
  }, [diagnoses, unfinishedDiagnosis]);

  useEffect(() => {
    debouncedErrorUpdater(errors);
  }, [errors]);

  const handleValidate = (key: string, data: any = diagnosisRow) => {
    if (!data) return;
    const isValid = validateSchema(MedicalHistoryRowSchema, key, data);
    setErrors((prev: any) => ({ ...prev, [key]: !isValid }));
  };

  const handleFieldUpdate = (field: string, value: string | IOption) => {
    setDiagnosisRow({ ...diagnosisRow, [field]: value });
    handleValidate(field, { ...diagnosisRow, [field]: value });
  };

  const handleCreateOrUpdateDiagnosis = async (field: string, value: string) => {
    const data = { ...diagnosisRow, [field]: value };
    if (!diagnosis) {
      //This means we are dealing with the unfinished diagnosis row
      dispatch(medicalHistoryActions.updateState({ key: 'unfinishedDiagnosis', data }));
    }
    const isValid = validateSync(MedicalHistoryRowSchema, data);
    if (!isValid) return;
    let response;
    if (medicalHistoryId) {
      if (diagnosis?.id) {
        response = await dispatch(
          medicalHistoryActions.updateDiagnosis({
            data,
            getAccessTokenSilently,
            planId: treatmentPlan?.id,
            medicalHistoryId,
            diagnosisId: diagnosis.id,
          }),
        );
      } else {
        response = await dispatch(
          medicalHistoryActions.createDiagnosis({
            data,
            getAccessTokenSilently,
            planId: treatmentPlan?.id,
            medicalHistoryId,
          }),
        );
      }
      if ('error' in response && field === 'year_diagnosis_made') {
        handleFieldUpdate('year_diagnosis_made', '');
      }
    } else {
      if (diagnosis?.id) {
        dispatch(
          medicalHistoryActions.updateUnsavedDiagnosis({
            data: diagnosisRow,
            id: diagnosis?.id,
          }),
        );
      } else {
        dispatch(
          medicalHistoryActions.addUnsavedDiagnosis({
            data: diagnosisRow,
            id: nextIndex,
          }),
        );
      }
    }
  };

  const handleBlur = (key: string, value: string) => {
    handleValidate(key);
    handleCreateOrUpdateDiagnosis(key, value);
  };

  const handleDeleteDiagnosis = () => {
    if (!treatmentPlan.id) return;
    if (medicalHistoryId) {
      if (diagnosis?.id) {
        dispatch(
          medicalHistoryActions.deleteDiagnosis({
            getAccessTokenSilently,
            planId: treatmentPlan.id,
            medicalHistoryId,
            diagnosisId: diagnosis.id,
          }),
        );
      } else {
        if (onRemove) {
          onRemove();
        }
      }
    } else {
      dispatch(
        medicalHistoryActions.removeUnsavedDiagnosis({
          data: diagnosisRow,
          id: diagnosis?.id,
        }),
      );
      if (onRemove) {
        onRemove();
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }} mt={2}>
      <Box width={35}>
        {editable && (
          <IconButton sx={{ p: 0, m: 0 }} disabled={loading} onClick={handleDeleteDiagnosis}>
            <DeleteIcon data-testid="deleteDiagnosis" sx={{ color: FORTA_BLUE.primary }} />
          </IconButton>
        )}
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={5}>
          <FHAutocomplete
            data-testid="selectDiagnosisManualInputDropDown"
            value={diagnosisRow?.diagnosis_name ?? ''}
            options={filteredDiagnosisOptions}
            placeholder="Select diagnosis"
            onChange={(_: ChangeEvent<HTMLSelectElement>, newValue: string) => {
              handleFieldUpdate('diagnosis_name', newValue);
              handleCreateOrUpdateDiagnosis('diagnosis_name', newValue);
            }}
            fullWidth
            isOptionEqualToValue={() => true}
            colorTheme={Themes.Primary}
            disabled={!editable}
            inputHeight={40}
            error={errors['diagnosis_name']}
          />
        </Grid>
        <Grid item xs={2.5}>
          <FHInputField
            data-testid="diagnosisProviderNameManualInputTextField"
            value={diagnosisRow?.provider_name ?? ''}
            fullWidth
            inputHeight={40}
            disabled={!editable}
            colorTheme={Themes.Primary}
            onChange={(e) => handleFieldUpdate('provider_name', e.target.value)}
            onBlur={(e) => handleBlur('provider_name', e.target.value)}
            error={errors['provider_name']}
          />
        </Grid>
        <Grid item xs={4.5}>
          <Grid container spacing={2}>
            <Grid item xs={5.5}>
              <FHInputField
                data-testid="diagnosisCityManualInputTextField"
                value={diagnosisRow?.city_diagnosis_made ?? ''}
                fullWidth
                inputHeight={40}
                disabled={!editable}
                colorTheme={Themes.Primary}
                onChange={(e) => handleFieldUpdate('city_diagnosis_made', e.target.value)}
                onBlur={(e) => handleBlur('city_diagnosis_made', e.target.value)}
                placeholder={'City'}
                sx={{ minWidth: 50 }}
                error={errors['city_diagnosis_made']}
              />
            </Grid>
            <Grid item xs={3.5}>
              <FHAutocomplete
                data-testid="diagnosisStateManualInputDropDown"
                value={diagnosisRow?.state_diagnosis_made ?? ''}
                options={usStates}
                placeholder={'State'}
                onChange={(_: ChangeEvent<HTMLSelectElement>, newValue: string) => {
                  handleFieldUpdate('state_diagnosis_made', newValue);
                  handleCreateOrUpdateDiagnosis('state_diagnosis_made', newValue);
                }}
                isOptionEqualToValue={() => true}
                colorTheme={Themes.Primary}
                disabled={!editable}
                disableClearable
                minWidth={50}
                inputHeight={40}
                error={errors['state_diagnosis_made']}
              />
            </Grid>
            <Grid item xs={3}>
              <FHInputField
                data-testid="diagnosisYearManualInputTextField"
                type="number"
                value={diagnosisRow?.year_diagnosis_made ?? ''}
                fullWidth
                disabled={!editable}
                colorTheme={Themes.Primary}
                onChange={(e) => handleFieldUpdate('year_diagnosis_made', e.target.value)}
                onBlur={(e) => handleBlur('year_diagnosis_made', e.target.value)}
                placeholder={'Year'}
                sx={{ minWidth: 50 }}
                inputHeight={40}
                error={errors['year_diagnosis_made']}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

export default MedicalHistoryRow;
